/**
 * Select.tsx — 自研下拉选择（移植自原型 select.jsx）
 *
 * <select> 的 drop-in 替代：支持 <option> / <optgroup> 子节点、键盘导航、type-to-search、portal 定位。
 * 候选很长时（如 200+ 商店地区）传 `searchable`：下拉顶部出筛选框，输入即时过滤 value + label 文本。
 */

import {
  Children,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '~/lib/icons';
import { useOwnedFocusScopePortalRef } from './focus-scope';

interface FlatItem {
  kind: 'item' | 'group';
  label?: ReactNode;
  labelText?: string;
  value?: string;
  disabled?: boolean;
  /** 所属 optgroup 序号（组头与其成员同号）；裸 option 无此字段 */
  groupIdx?: number;
}

export interface SelectChangeEvent {
  target: { value: string; name?: string };
}

interface SelectProps {
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (e: SelectChangeEvent) => void;
  disabled?: boolean;
  children?: ReactNode;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  name?: string;
  /** 候选多时开启：下拉顶部渲染筛选框，输入按空格分词逐词匹配 option 的 value + label 文本 */
  searchable?: boolean;
  /** 筛选框 placeholder（仅 searchable 生效）*/
  searchPlaceholder?: string;
}

/** 查询串分词：忽略大小写与多余空格，多词全部命中才算匹配（「me 黑」「黑山 me」都能中 ME 黑山）*/
function tokenizeQuery(query: string): string[] {
  return query.toLowerCase().trim().split(/\s+/).filter(Boolean);
}

/**
 * 按查询词过滤扁平项：item 匹配 value + label 文本；group 头只在其下还剩 item 时保留。
 * 组件外的纯函数——输入变化时要用「新 query 算出的可见项」立即重定位 activeIdx，不能等 memo。
 */
function filterFlatItems(items: FlatItem[], tokens: string[]): FlatItem[] {
  if (tokens.length === 0) return items;
  const kept = items.filter((it) => {
    if (it.kind === 'group') return true;
    const haystack = `${it.value ?? ''} ${it.labelText ?? ''}`.toLowerCase();
    return tokens.every((t) => haystack.includes(t));
  });
  // 组头保留条件：紧跟其后的仍是**本组**成员。只判「下一条是 item」会在 optgroup 与裸 option
  // 混排时把别组 / 无组的项挂到上一个组头下面。
  return kept.filter(
    (it, i) =>
      it.kind !== 'group' ||
      (kept[i + 1]?.kind === 'item' && kept[i + 1]?.groupIdx === it.groupIdx),
  );
}

function renderToText(node: ReactNode): string {
  if (node == null || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(renderToText).join('');
  // biome-ignore lint/suspicious/noExplicitAny: 读取任意 React 元素 children
  const props = (node as any)?.props;
  if (props && props.children !== undefined) return renderToText(props.children);
  return '';
}

function parseChildren(children: ReactNode): FlatItem[] {
  const flat: FlatItem[] = [];
  let groupSeq = 0;
  Children.forEach(children, (ch) => {
    // biome-ignore lint/suspicious/noExplicitAny: 解析 option/optgroup 元素
    const el = ch as any;
    if (!el || !el.props) return;
    if (el.type === 'optgroup') {
      const groupIdx = groupSeq++;
      flat.push({ kind: 'group', label: el.props.label, groupIdx });
      Children.forEach(el.props.children, (sub) => {
        // biome-ignore lint/suspicious/noExplicitAny: 解析 option 元素
        const s = sub as any;
        if (!s || !s.props) return;
        const lab = renderToText(s.props.children);
        flat.push({
          kind: 'item',
          value: s.props.value !== undefined ? String(s.props.value) : lab,
          label: s.props.children,
          labelText: lab,
          disabled: !!s.props.disabled,
          groupIdx,
        });
      });
      return;
    }
    const lab = renderToText(el.props.children);
    flat.push({
      kind: 'item',
      value: el.props.value !== undefined ? String(el.props.value) : lab,
      label: el.props.children,
      labelText: lab,
      disabled: !!el.props.disabled,
    });
  });
  return flat;
}

export function Select({
  value,
  defaultValue,
  onChange,
  disabled,
  children,
  style = {},
  className = 'input',
  placeholder,
  name,
  searchable = false,
  searchPlaceholder = '输入关键字筛选…',
}: SelectProps) {
  const items = useMemo(() => parseChildren(children), [children]);
  // itemList 进了下方 open 定位 effect 的依赖数组：必须 memo 出稳定引用，
  // 否则每次渲染都是新数组 → effect 反复重订阅 + setPos 新对象 → 无限渲染循环。
  const itemList = useMemo(() => items.filter((i) => i.kind === 'item'), [items]);

  const controlled = value !== undefined;
  const firstUsable = itemList.find((i) => !i.disabled);
  const [internal, setInternal] = useState<string>(() => {
    if (defaultValue !== undefined) return String(defaultValue);
    return firstUsable?.value ?? '';
  });
  const current = controlled ? String(value ?? '') : internal;
  const currentItem = itemList.find((i) => i.value === current);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxH: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(() =>
    Math.max(
      0,
      itemList.findIndex((i) => i.value === current),
    ),
  );
  const typeBuf = useRef({ q: '', t: 0 });
  // 弹层挂在 body：注册成模态焦点域的 owned portal，筛选框才进 Dialog 的 Tab 序列、关闭时焦点回 trigger
  const ownedPortalRef = useOwnedFocusScopePortalRef<HTMLDivElement>(triggerRef, popoverRef, true);
  const [query, setQuery] = useState('');
  const listboxId = useId();
  // 可见项 = 过滤后的清单；键盘导航 / activeIdx / 渲染全部以它为准，itemList 只用于取当前值与弹层高度估算
  const visible = useMemo(
    () => (searchable ? filterFlatItems(items, tokenizeQuery(query)) : items),
    [items, searchable, query],
  );
  const visibleItems = useMemo(() => visible.filter((i) => i.kind === 'item'), [visible]);
  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const pick = useCallback(
    (item: FlatItem) => {
      if (item.disabled || item.value === undefined) return;
      if (!controlled) setInternal(item.value);
      onChange?.({ target: { value: item.value, name } });
      close();
    },
    [controlled, name, onChange, close],
  );

  // 每次打开都重置筛选并把高亮落到当前值（打开态下不再受旧 query 影响）
  const openMenu = useCallback(() => {
    setQuery('');
    const selected = itemList.findIndex((i) => i.value === current);
    setActiveIdx(
      selected >= 0
        ? selected
        : Math.max(
            0,
            itemList.findIndex((i) => !i.disabled),
          ),
    );
    setOpen(true);
  }, [itemList, current]);

  // 过滤后高亮要立刻落到新清单的首个可用项，否则回车会选中已被过滤掉的项
  const changeQuery = useCallback(
    (next: string) => {
      setQuery(next);
      const nextItems = filterFlatItems(items, tokenizeQuery(next)).filter(
        (i) => i.kind === 'item',
      );
      setActiveIdx(
        Math.max(
          0,
          nextItems.findIndex((i) => !i.disabled),
        ),
      );
    },
    [items],
  );

  // 弹层要等定位算完才挂载，焦点只能在筛选框挂载那一刻给（effect 里 ref 还是空的）
  const focusSearchOnMount = useCallback((el: HTMLInputElement | null) => {
    el?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = Math.max(r.width, 160);
      const margin = 8;
      const popMaxH = Math.min(320, window.innerHeight - r.bottom - 12);
      let top = r.bottom + 4;
      if (top + Math.min(320, itemList.length * 32 + 8) > window.innerHeight - margin) {
        const above = r.top - Math.min(320, itemList.length * 32 + 8) - 4;
        if (above > margin) top = above;
      }
      let left = r.left;
      if (left + w > window.innerWidth - margin) left = window.innerWidth - w - margin;
      setPos({ top, left, width: w, maxH: popMaxH });
    };
    update();
    const onDoc = (e: MouseEvent) => {
      if (
        popoverRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      )
        return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      // 输入法组字期间的按键归输入法：中文候选词用 Enter 提交、用方向键翻页，
      // 不守这道闸，拼「摩洛哥」按下的那个 Enter 会被当成「选中当前高亮项」直接落值。
      if (e.isComposing || e.keyCode === 229) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        // 不 stop 的话事件继续冒到 window，Dialog 的 Esc 监听会把整个弹窗一起关掉、
        // 丢弃未保存编辑（Dialog 只判 e.key，不看 defaultPrevented）。
        e.stopPropagation();
        close();
        triggerRef.current?.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (visibleItems.length === 0) return;
        setActiveIdx((i) => {
          let next = i;
          do {
            next = (next + 1) % visibleItems.length;
          } while (visibleItems[next]?.disabled && next !== i);
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (visibleItems.length === 0) return;
        setActiveIdx((i) => {
          let next = i;
          do {
            next = (next - 1 + visibleItems.length) % visibleItems.length;
          } while (visibleItems[next]?.disabled && next !== i);
          return next;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = visibleItems[activeIdx];
        if (item && !item.disabled) pick(item);
      } else if (!searchable && e.key.length === 1) {
        // searchable 时单字符归筛选框，不再走 type-ahead 跳转

        const now = Date.now();
        if (now - typeBuf.current.t > 600) typeBuf.current.q = '';
        typeBuf.current.t = now;
        typeBuf.current.q += e.key.toLowerCase();
        const q = typeBuf.current.q;
        const found = visibleItems.findIndex(
          (it) => !it.disabled && it.labelText?.toLowerCase().startsWith(q),
        );
        if (found >= 0) setActiveIdx(found);
      }
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, activeIdx, itemList, visibleItems, searchable, close, pick]);

  useEffect(() => {
    if (!open) return;
    const el = popoverRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const displayLabel = currentItem ? (
    currentItem.label
  ) : (
    <span style={{ color: 'var(--fg-faint)' }}>{placeholder || '请选择…'}</span>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          paddingRight: 28,
          position: 'relative',
          ...style,
        }}
        onClick={() => {
          if (disabled) return;
          if (open) close();
          else openMenu();
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 'var(--text-sm)',
            color: currentItem ? 'var(--fg)' : undefined,
          }}
        >
          {displayLabel}
        </span>
        <span
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--fg-muted)',
            display: 'inline-flex',
          }}
        >
          <Icons.IconChevDown size={13} />
        </span>
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={ownedPortalRef}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              minWidth: pos.width,
              maxWidth: 420,
              maxHeight: pos.maxH,
              background: 'var(--bg-elev)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              zIndex: 200,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              animation: 'fade-in 0.1s ease',
            }}
          >
            {searchable && (
              <div
                style={{
                  padding: 6,
                  borderBottom: '1px solid var(--border)',
                  position: 'relative',
                  flexShrink: 0,
                }}
              >
                <Icons.IconSearch
                  size={12}
                  style={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--fg-muted)',
                    pointerEvents: 'none',
                    display: 'block',
                  }}
                />
                <input
                  ref={focusSearchOnMount}
                  className="input"
                  value={query}
                  onChange={(e) => changeQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label="筛选选项"
                  /* 焦点始终在筛选框，读屏靠 activedescendant 播报当前高亮项 */
                  role="combobox"
                  aria-expanded
                  aria-controls={listboxId}
                  aria-autocomplete="list"
                  aria-activedescendant={
                    visibleItems[activeIdx] && !visibleItems[activeIdx].disabled
                      ? `${listboxId}-o-${activeIdx}`
                      : undefined
                  }
                  style={{ width: '100%', height: 30, fontSize: 12.5, paddingLeft: 28 }}
                />
              </div>
            )}
            <div
              id={listboxId}
              role="listbox"
              style={{ padding: 4, overflow: 'auto', flex: 1, minHeight: 0 }}
            >
              {visible.map((it, i) => {
                if (it.kind === 'group') {
                  return (
                    <div
                      key={`g-${i}`}
                      style={{
                        padding: '8px 10px 4px',
                        fontSize: 10.5,
                        fontWeight: 500,
                        color: 'var(--fg-faint)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {it.label}
                    </div>
                  );
                }
                const itemIdx = visibleItems.indexOf(it);
                const isActive = itemIdx === activeIdx;
                const isSelected = it.value === current;
                return (
                  <div
                    key={`i-${i}`}
                    id={`${listboxId}-o-${itemIdx}`}
                    data-idx={itemIdx}
                    onClick={() => pick(it)}
                    onMouseEnter={() => !it.disabled && setActiveIdx(itemIdx)}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={it.disabled}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12.5,
                      cursor: it.disabled ? 'not-allowed' : 'pointer',
                      color: it.disabled ? 'var(--fg-faint)' : 'var(--fg)',
                      background: isActive && !it.disabled ? 'var(--bg-subtle)' : 'transparent',
                      position: 'relative',
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.label}
                    </span>
                    {isSelected && (
                      <Icons.IconCheck
                        size={12}
                        style={{ color: 'var(--accent)', flexShrink: 0 }}
                      />
                    )}
                  </div>
                );
              })}
              {searchable && visibleItems.length === 0 && (
                <div
                  style={{
                    padding: '10px 10px 12px',
                    fontSize: 12,
                    color: 'var(--fg-faint)',
                    textAlign: 'center',
                  }}
                >
                  {query ? '无匹配项' : '暂无选项'}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
